import React, { useMemo, useState } from 'react';
import { Badge, Button, Form } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import type { ChangeWorkGroup } from '../../../types/changes';
import {
  APPROVAL_TRACKS,
  TRACK_CAMUD,
  TRACK_GESTAO,
  TRACK_RISCOS,
  type ApprovalPlanDraft,
  type ApprovalPlanPerson,
  type ApprovalTrack,
} from '../changeApprovalPlan';

interface Props {
  draft: ApprovalPlanDraft;
  areas: ChangeWorkGroup[];
  tracks?: ReadonlyArray<{ track: ApprovalTrack; label: string }>;
  disabled?: boolean;
  onChange: (draft: ApprovalPlanDraft) => void;
}

const emptyQuery = (): Record<ApprovalTrack, string> => ({
  [TRACK_RISCOS]: '',
  [TRACK_CAMUD]: '',
  [TRACK_GESTAO]: '',
});

/** Ao abrir a tela, lista de áreas visível; o usuário pode recolher com "Ocultar áreas". */
const initialListOpen = (): Record<ApprovalTrack, boolean> => ({
  [TRACK_RISCOS]: true,
  [TRACK_CAMUD]: true,
  [TRACK_GESTAO]: false,
});

const ChangeApprovalPlanEditor: React.FC<Props> = ({
  draft,
  areas,
  tracks = APPROVAL_TRACKS,
  disabled = false,
  onChange,
}) => {
  const { showConfirm } = useModal();
  const [query, setQuery] = useState<Record<ApprovalTrack, string>>(emptyQuery);
  const [personDraft, setPersonDraft] = useState<Record<ApprovalTrack, { groupId: string; assigneeId: string }>>({
    [TRACK_RISCOS]: { groupId: '', assigneeId: '' },
    [TRACK_CAMUD]: { groupId: '', assigneeId: '' },
    [TRACK_GESTAO]: { groupId: '', assigneeId: '' },
  });
  const [listOpen, setListOpen] = useState<Record<ApprovalTrack, boolean>>(initialListOpen);

  const areasById = useMemo(() => {
    const map = new Map<number, ChangeWorkGroup>();
    for (const area of areas) map.set(area.group_id, area);
    return map;
  }, [areas]);

  const toggleTrack = (track: ApprovalTrack, enabled: boolean) => {
    onChange({
      enabled: { ...draft.enabled, [track]: enabled },
      groupIds: { ...draft.groupIds, [track]: enabled ? draft.groupIds[track] : [] },
      people: { ...draft.people, [track]: enabled ? draft.people[track] : [] },
    });
    if (enabled) {
      setListOpen((prev) => ({ ...prev, [track]: draft.groupIds[track].length === 0 }));
    }
  };

  const removeTrack = async (track: ApprovalTrack, label: string) => {
    const ok = await showConfirm({
      title: 'Remover grupo',
      message: `Remover ${label} deste plano? As áreas escolhidas neste grupo saem da seleção.`,
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    toggleTrack(track, false);
  };

  const toggleArea = (track: ApprovalTrack, groupId: number, checked: boolean) => {
    const current = draft.groupIds[track];
    const groupIds = checked
      ? current.includes(groupId)
        ? current
        : [...current, groupId]
      : current.filter((id) => id !== groupId);
    onChange({
      enabled: { ...draft.enabled, [track]: true },
      groupIds: { ...draft.groupIds, [track]: groupIds },
      people: draft.people,
    });
  };

  const addPerson = (track: ApprovalTrack) => {
    const groupId = Number(personDraft[track].groupId);
    const assigneeId = Number(personDraft[track].assigneeId);
    if (!groupId || !assigneeId) return;
    const area = areasById.get(groupId);
    const member = (area?.members || []).find((item) => item.user_id === assigneeId);
    const current = draft.people[track] || [];
    if (current.some((item) => item.group_id === groupId && item.assignee_id === assigneeId)) {
      return;
    }
    const person: ApprovalPlanPerson = {
      group_id: groupId,
      group_name: area?.name || null,
      assignee_id: assigneeId,
      assignee_name: member?.name || null,
    };
    onChange({
      enabled: { ...draft.enabled, [track]: true },
      groupIds: draft.groupIds,
      people: { ...draft.people, [track]: [...current, person] },
    });
    setPersonDraft((prev) => ({ ...prev, [track]: { groupId: '', assigneeId: '' } }));
  };

  const removePerson = (track: ApprovalTrack, person: ApprovalPlanPerson) => {
    onChange({
      enabled: draft.enabled,
      groupIds: draft.groupIds,
      people: {
        ...draft.people,
        [track]: (draft.people[track] || []).filter(
          (item) => !(item.group_id === person.group_id && item.assignee_id === person.assignee_id),
        ),
      },
    });
  };

  return (
    <div>
      <p className="text-muted small mb-2">
        Ligue o grupo de área, escolha áreas e/ou pessoas e salve. Membro da área decide o slot do
        grupo; no disparo de pessoa, só o destinatário vota. O <strong>Gestor de mudanças</strong> é
        só um interruptor (pessoa com a flag), sem lista de áreas.
      </p>
      <div className="row g-2">
        {tracks.map((item) => {
          const enabled = draft.enabled[item.track];
          const isGestao = item.track === TRACK_GESTAO;

          if (isGestao) {
            return (
              <div key={item.track} className="col-12 col-lg-6">
                <div className="border rounded p-2 h-100" data-testid="approval-gestao-toggle">
                  <Form.Check
                    type="switch"
                    id={`approval-track-${item.track}`}
                    label={<strong>{item.label}</strong>}
                    checked={enabled}
                    disabled={disabled}
                    onChange={(e) => toggleTrack(item.track, e.target.checked)}
                  />
                  {enabled ? (
                    <p className="small text-muted mb-0 mt-2">
                      Aprova Admin ou quem tem a flag Gestor de mudanças. Não usa grupo nem área.
                    </p>
                  ) : (
                    <p className="small text-muted mb-0 mt-2">Desligado nesta mudança.</p>
                  )}
                </div>
              </div>
            );
          }

          const selectedIds = draft.groupIds[item.track];
          const selectedPeople = draft.people[item.track] || [];
          const personGroupId = Number(personDraft[item.track].groupId);
          const personMembers = personGroupId
            ? areasById.get(personGroupId)?.members || []
            : [];
          const term = query[item.track].trim().toLowerCase();
          const visibleAreas = term
            ? areas.filter((area) => (area.name || '').toLowerCase().includes(term))
            : areas;
          // Com 0–1 área a lista fica visível para marcar CSI, COPER etc.; com 2+ obedece Ocultar/expandir.
          const showList =
            enabled && (selectedIds.length <= 1 || listOpen[item.track]);

          return (
            <div key={item.track} className="col-12 col-lg-6">
              <div className="border rounded p-2 h-100">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <Form.Check
                    type="switch"
                    id={`approval-track-${item.track}`}
                    label={<strong>{item.label}</strong>}
                    checked={enabled}
                    disabled={disabled}
                    onChange={(e) => toggleTrack(item.track, e.target.checked)}
                  />
                  {enabled && !disabled ? (
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() =>
                          setListOpen((prev) => ({ ...prev, [item.track]: !prev[item.track] }))
                        }
                      >
                        {showList
                          ? 'Ocultar áreas'
                          : `Mostrar todas as áreas (${selectedIds.length} selecionada(s) · ${areas.length} no catálogo)`}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => void removeTrack(item.track, item.label)}
                      >
                        Remover grupo
                      </Button>
                    </div>
                  ) : null}
                </div>

                {enabled ? (
                  <div className="mt-2">
                    {selectedIds.length ? (
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {selectedIds.map((groupId) => {
                          const area = areasById.get(groupId);
                          const label = area?.name || `Área #${groupId}`;
                          return (
                            <Badge
                              key={`${item.track}-chip-${groupId}`}
                              bg="primary"
                              className="d-inline-flex align-items-center gap-1 fw-normal"
                            >
                              {label}
                              {!disabled ? (
                                <button
                                  type="button"
                                  className="btn-close btn-close-white"
                                  style={{ fontSize: 8 }}
                                  aria-label={`Remover ${label}`}
                                  onClick={() => toggleArea(item.track, groupId, false)}
                                />
                              ) : null}
                            </Badge>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="small text-muted mb-2">Nenhuma área escolhida neste grupo.</div>
                    )}

                    {!showList && selectedIds.length && !disabled ? (
                      <p className="small text-muted mb-2">
                        Várias áreas podem aprovar em <strong>{item.label}</strong>. Use o botão acima
                        para marcar outras além das que já estão no plano.
                      </p>
                    ) : null}

                    {showList ? (
                      areas.length === 0 ? (
                        <div className="small text-muted">
                          Cadastre áreas em Configurações → Cadastros de mudança.
                        </div>
                      ) : (
                        <>
                          <Form.Control
                            size="sm"
                            className="mb-2"
                            placeholder="Buscar área"
                            value={query[item.track]}
                            onChange={(e) =>
                              setQuery((prev) => ({ ...prev, [item.track]: e.target.value }))
                            }
                            aria-label={`Buscar área em ${item.label}`}
                          />
                          <div
                            className="row g-1"
                            style={{ maxHeight: 168, overflowY: 'auto' }}
                            data-testid={`approval-area-list-${item.track}`}
                          >
                            {visibleAreas.length ? (
                              visibleAreas.map((area) => (
                                <div key={`${item.track}-${area.group_id}`} className="col-12 col-sm-6">
                                  <Form.Check
                                    id={`approval-area-${item.track}-${area.group_id}`}
                                    type="checkbox"
                                    className="small mb-0"
                                    disabled={disabled}
                                    checked={selectedIds.includes(area.group_id)}
                                    label={
                                      <span>
                                        {area.name || `Área #${area.group_id}`}
                                        {(area.members || []).length ? (
                                          <span className="text-muted">
                                            {' '}
                                            · {(area.members || []).length}
                                          </span>
                                        ) : null}
                                      </span>
                                    }
                                    onChange={(e) => toggleArea(item.track, area.group_id, e.target.checked)}
                                  />
                                </div>
                              ))
                            ) : (
                              <div className="small text-muted px-1">Nenhuma área com esse nome.</div>
                            )}
                          </div>
                        </>
                      )
                    ) : null}

                    <div className="mt-3">
                      <div className="small fw-semibold mb-1">Pessoa específica</div>
                      {selectedPeople.length ? (
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {selectedPeople.map((person) => {
                            const label = `${person.assignee_name || `Usuário #${person.assignee_id}`} · ${
                              person.group_name || `Área #${person.group_id}`
                            }`;
                            return (
                              <Badge
                                key={`${item.track}-person-${person.group_id}-${person.assignee_id}`}
                                bg="secondary"
                                className="d-inline-flex align-items-center gap-1 fw-normal"
                              >
                                {label}
                                {!disabled ? (
                                  <button
                                    type="button"
                                    className="btn-close btn-close-white"
                                    style={{ fontSize: 8 }}
                                    aria-label={`Remover ${label}`}
                                    onClick={() => removePerson(item.track, person)}
                                  />
                                ) : null}
                              </Badge>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="small text-muted mb-2">Nenhuma pessoa disparada neste grupo.</div>
                      )}
                      {!disabled ? (
                        <div className="row g-2 align-items-end">
                          <div className="col-12 col-sm-5">
                            <Form.Select
                              size="sm"
                              aria-label={`Grupo da pessoa em ${item.label}`}
                              value={personDraft[item.track].groupId}
                              onChange={(e) =>
                                setPersonDraft((prev) => ({
                                  ...prev,
                                  [item.track]: { groupId: e.target.value, assigneeId: '' },
                                }))
                              }
                            >
                              <option value="">Grupo</option>
                              {areas.map((area) => (
                                <option key={`${item.track}-person-group-${area.group_id}`} value={area.group_id}>
                                  {area.name || `Área #${area.group_id}`}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className="col-12 col-sm-5">
                            <Form.Select
                              size="sm"
                              aria-label={`Pessoa em ${item.label}`}
                              value={personDraft[item.track].assigneeId}
                              disabled={!personGroupId}
                              onChange={(e) =>
                                setPersonDraft((prev) => ({
                                  ...prev,
                                  [item.track]: { ...prev[item.track], assigneeId: e.target.value },
                                }))
                              }
                            >
                              <option value="">Pessoa</option>
                              {personMembers.map((member) => (
                                <option key={`${item.track}-person-${member.user_id}`} value={member.user_id}>
                                  {member.name || `Usuário #${member.user_id}`}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <div className="col-12 col-sm-2">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={!personDraft[item.track].groupId || !personDraft[item.track].assigneeId}
                              onClick={() => addPerson(item.track)}
                            >
                              Adicionar
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChangeApprovalPlanEditor;
